<template>
  <section class="form-section">
    <h1 class="form-section__title">
      <span>{{ $t('PUBLISHER_ACCOUNT.NATIONAL_INFO') }}</span>
      <a
        v-if="$auth.check(['publisher', 'changeFinanceInfo']) && !isVerified"
        v-show="!isEditableNationalInfo"
        class="form-section__action-btn"
        role="button"
        @click.prevent="isEditableNationalInfo = true"
      >
        <i class="el-icon-edit form-section__action-btn-icon" />
        <span class="form-section__edit-title">
          {{ $t('EDIT') }}
        </span>
      </a>
    </h1>

    <div class="row align-justify">
      <div class="small-12 large-4 columns">
        <p class="form-section__description">
          <span v-if="!isVerified">
            {{ $t('PUBLISHER_ACCOUNT.NATIONAL_INFO_DESC') }}
          </span>
          <RouterLinkFormat
            :links="{
              support: { to: { path: '/support' }, text: $t('SUPPORT') },
            }"
          >
            {{ $t('EDIT_DISABLED_DESC') }}
          </RouterLinkFormat>
        </p>
      </div>

      <div class="small-12 large-7 columns">
        <ElForm
          ref="nationalInfo"
          class="card"
          :rules="rules"
          :model="legalInfoModel"
          label-position="top"
          @submit.native.prevent="updateNationalInfo"
        >
          <div
            v-if="
              $auth.check(['publisher', 'changeFinanceInfo']) && !isVerified
            "
            v-show="isEditableNationalInfo"
          >
            <div class="card__block">
              <ElFormItem
                :label="$t('PUBLISHER_ACCOUNT.PASSPORT_IMAGE')"
                required
              >
                <Droppable
                  v-if="hasProps(legalInfoModel)"
                  :has-preview="true"
                  :has-progress="true"
                  file-types="image/*"
                  :upload-url="uploadPaths.passport"
                  :image-url="legalInfoModel.passport_image.url"
                  @uploaded="updatePassportImage"
                >
                  <div slot="label">
                    {{ $t('PUBLISHER_ACCOUNT.DRAG_PASSPORT_IMAGE') }}
                  </div>
                </Droppable>
              </ElFormItem>

              <ElFormItem
                :label="$t('PUBLISHER_ACCOUNT.FULL_NAME')"
                prop="name"
              >
                <ElInput v-model="legalInfoModel.name" name="name" />
                <p class="el-form-item__help">
                  {{ $t('PUBLISHER_ACCOUNT.NAME_HELP_TEXT') }}
                </p>
              </ElFormItem>

              <ElFormItem
                :label="$t('PUBLISHER_ACCOUNT.PASSPORT_CODE')"
                prop="passport_code"
              >
                <ElInput
                  v-model="legalInfoModel.passport_code"
                  name="passport_code"
                />
                <p class="el-form-item__help">
                  {{ $t('PUBLISHER_ACCOUNT.PASSPORT_CODE_HELP_TEXT') }}
                </p>
              </ElFormItem>

              <ElButton type="primary" native-type="submit" size="large">
                {{ $t('SAVE') }}
              </ElButton>

              <ElButton
                type="text"
                @click.native="isEditableNationalInfo = false"
              >
                {{ $t('CANCEL') }}
              </ElButton>
            </div>
          </div>

          <div v-show="!isEditableNationalInfo">
            <div class="card__block">
              <dl class="list list--definition list--clean">
                <dt>{{ $t('PUBLISHER_ACCOUNT.PASSPORT_IMAGE') }}</dt>
                <dd>
                  <img
                    v-if="hasProps(legalInfo)"
                    class="droppable__image"
                    :src="legalInfo.passport_image.url"
                  />
                </dd>

                <dt>{{ $t('PUBLISHER_ACCOUNT.FULL_NAME') }}</dt>
                <dd>{{ legalInfo.name }}</dd>

                <dt>{{ $t('PUBLISHER_ACCOUNT.PASSPORT_CODE') }}</dt>
                <dd>{{ legalInfo.passport_code }}</dd>
              </dl>
            </div>
          </div>
        </ElForm>
      </div>
    </div>
  </section>
</template>

<script>
import Droppable from '@@/layout/Droppable';
import { Publishers } from '@/api/api';

import { nonEmptyProps, hasProps } from '@/utils/object';

import form from '@/mixins/form';

import RouterLinkFormat from '@@/common/RouterLinkFormat';

export default {
  components: {
    Droppable,
    RouterLinkFormat,
  },
  mixins: [form],

  props: {
    legalInfo: {
      type: Object,
      default() {
        return {};
      },
    },

    isVerified: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      legalInfoModel: Object.assign({}, this.legalInfo),
      uploadPaths: Publishers.uploadPaths,
      isEditableNationalInfo: false,
    };
  },

  computed: {
    nationalInfo() {
      const model = this.legalInfoModel;

      return nonEmptyProps({
        name: model.name,
        passport_code: model.passport_code,
        passport_image: model.passport_image.id,
      });
    },

    rules() {
      return {
        name: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        passport_code: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        passport_image: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
      };
    },
  },

  watch: {
    legalInfo() {
      this.legalInfoModel = Object.assign({}, this.legalInfo);
    },
  },

  methods: {
    hasProps,

    updatePassportImage(src) {
      this.legalInfoModel.passport_image.id = src;
    },

    updateNationalInfo() {
      if (
        this.$auth.check(['publisher', 'changeFinanceInfo']) &&
        this.isValid('nationalInfo')
      ) {
        return (
          Publishers.updateLegal({ type: 'identification' }, this.nationalInfo)
            .then(
              ({
                body: { message, identification_info: identificationInfo },
              }) => {
                this.legalInfoModel = Object.assign({}, identificationInfo);

                this.$emit('updatedLegalInfo', this.legalInfoModel);
                this.$message.success(message);
              },
            )
            // switches to the read-only mode
            .then(() => {
              this.isEditableNationalInfo = false;
            })
            .catch(({ body: { message } = {} }) => {
              if (message) {
                this.$message.error(message);
              }
            })
        );
      }
      return false;
    },
  },
};
</script>
